import React, { useEffect, useState } from 'react';
import { Button, Switch } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { Close } from '@material-ui/icons';
import ModalDlg from 'components/Basic/ModalDlg';
import Loading from 'components/Basic/Loading';
import toast from 'components/Basic/Toast';
import {
  useCreatePaymentMethodDiscountMutation,
  useLazyGetPaymentMethodDiscountsQuery,
  useUpdatePaymentMethodDiscountMutation,
} from 'core/catalog/CatalogService';

const ZIGI_PAYMENT_METHOD = 'ZIGI';
// The row changed from elsewhere (created meanwhile or removed), so the modal reloads it.
const OUTDATED_STATUSES = [404, 409];

const DiscountConfigurationModal = ({ isShowModal, onCloseDiscountModal, item }) => {
  const intl = useIntl();
  const [discount, setDiscount] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [getDiscounts, { isFetching }] = useLazyGetPaymentMethodDiscountsQuery();
  const [createDiscount, { isLoading: isCreating }] = useCreatePaymentMethodDiscountMutation();
  const [updateDiscount, { isLoading: isUpdating }] = useUpdatePaymentMethodDiscountMutation();

  const reload = () => setLoadAttempt(attempt => attempt + 1);

  useEffect(() => {
    if (!isShowModal) return undefined;
    let current = true;
    setDiscount(null);
    setLoadFailed(false);
    const request = getDiscounts({ itemId: item.id, paymentMethod: ZIGI_PAYMENT_METHOD });
    request
      .unwrap()
      .then(res => current && setDiscount(res.rows[0] ?? null))
      .catch(() => current && setLoadFailed(true));
    return () => {
      current = false;
      request.abort();
    };
  }, [isShowModal, item.id, loadAttempt, getDiscounts]);

  const onToggle = async enabled => {
    try {
      const res = discount
        ? await updateDiscount({ id: discount.id, enabled }).unwrap()
        : await createDiscount({
            itemId: item.id,
            itemCode: item.productCode,
            paymentMethod: ZIGI_PAYMENT_METHOD,
          }).unwrap();
      setDiscount(res.discount);
      toast.success({ title: intl.formatMessage({ id: 'Discount updated successfully.' }) });
    } catch (error) {
      if (OUTDATED_STATUSES.includes(error?.status)) {
        toast.info({ title: intl.formatMessage({ id: 'The discount changed elsewhere and was reloaded.' }) });
        reload();
        return;
      }
      toast.error({ title: intl.formatMessage({ id: 'Could not update the discount.' }) });
    }
  };

  const hasProductCode = item.productCode > 0;
  const hint = (() => {
    if (loadFailed) return 'Could not load the discount for this product.';
    if (!hasProductCode) return 'This product has no product code, so it cannot have a discount.';
    return 'ZIGI discount description';
  })();

  return (
    <ModalDlg
      className="discount_config_modal"
      visible={isShowModal}
      handleCancel={onCloseDiscountModal}
      width={460}
      isCenter
    >
      <Loading visible={isFetching || isCreating || isUpdating} />
      <div className="modal_header">
        <div className="name_wrapper">
          <h3>
            <FormattedMessage id="Payment method discount" />
          </h3>
          <div className="name_wrapper_id">
            <FormattedMessage id="OfferId" values={{ id: item.id }} />
          </div>
          <div className="close_modal" onClick={onCloseDiscountModal}>
            <Close />
          </div>
        </div>
      </div>
      <div className="discount_body">
        <div className="discount_switch">
          <Switch
            id="discount_zigi_switch"
            checked={!!discount?.enabled}
            disabled={!hasProductCode || loadFailed || isCreating || isUpdating}
            onChange={onToggle}
          />
          <label htmlFor="discount_zigi_switch">
            <FormattedMessage id="Apply the ZIGI discount to this product" />
          </label>
        </div>
        <p className="description">
          <FormattedMessage id={hint} />
        </p>
        {loadFailed && (
          <Button id="discount_zigi_retry" onClick={reload}>
            <FormattedMessage id="Retry" />
          </Button>
        )}
      </div>
    </ModalDlg>
  );
};

export default DiscountConfigurationModal;
